import React, { useCallback, useEffect } from "react";
import { track } from "../../../firebase/firebase";
/**
 * White‑wall lightbox: photograph, caption, arrow‑key / swipe navigation.
 * Keeps the existing `image_enlarged` analytics event.
 *
 * NOTE on image protection: onContextMenu/draggable below are friction only
 * (deter casual right-click-save / drag-save). They do not stop DevTools,
 * View Source, or screenshots — there is no web API that can black out a
 * screenshot. Treat this as a UX nudge, not a security control.
 */
export default function Lightbox({ items, index, onClose, onNavigate }) {
  const count = items.length;
  const item = items[index];

  const go = useCallback(
    (dir) => onNavigate((index + dir + count) % count),
    [index, count, onNavigate]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [go, onClose]);

  useEffect(() => {
    track("image_enlarged", { image_name: item?.caption || item?.name });
    if (count < 2) return;
    [(index + 1) % count, (index - 1 + count) % count].forEach((i) => {
      const img = new Image();
      img.src = items[i].url;
    });
  }, [index, items, count, item]);

  if (!item) return null;

  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={item.caption || "Photograph"}
      onContextMenu={(e) => e.preventDefault()}
    >
      <button
        className="lightbox-close"
        aria-label="Close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        &times;
      </button>

      <div className="lightbox-stage" onClick={onClose}>
        <img
          src={item.url}
          alt={item.caption || ""}
          draggable={false}
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      {count > 1 && (
        <>
          <button
            className="lightbox-nav lightbox-nav--prev"
            aria-label="Previous photograph"
            onClick={() => go(-1)}
          >
            &#8592;
          </button>
          <button
            className="lightbox-nav lightbox-nav--next"
            aria-label="Next photograph"
            onClick={() => go(1)}
          >
            &#8594;
          </button>
        </>
      )}

      <div className="lightbox-bar">
        <span className="lightbox-caption">{item.caption}</span>
        <span className="lightbox-counter">
          {index + 1} / {count}
        </span>
      </div>
    </div>
  );
}